import React from "react"
import { Flex } from "../Flex"
import { Icon } from "../Icon"
import { HeaderStyled, PanelInnerStyled, PanelStyled } from "./styled"
import { AccordionHeaderProps, AccordionPanelProps } from "./types"

export interface AccordionProps {
	activeId?: string
	controlled?: boolean
	children?: React.ReactNode
}

export interface AccordionComponent {
	Header: React.FC<React.HTMLProps<HTMLButtonElement> & AccordionHeaderProps>
	Panel: React.FC<AccordionPanelProps>
}

const AccordionContext = React.createContext({
	activeId: "",
	setActiveId: (id: string) => { },
	controlled: false,
})

export const useAccordionActiveAccordion = (initialActiveId?: string) => {
	const [activeId, setActiveId] = React.useState(initialActiveId || "")
	return { activeId, setActiveId }
}

const Accordion: React.FC<AccordionProps> & AccordionComponent = (props) => {
	const { activeId, setActiveId } = useAccordionActiveAccordion(
		props.activeId || ""
	)
	const internalActiveId = props.controlled ? props.activeId || "" : activeId

	return (
		<AccordionContext.Provider
			value={{
				activeId: internalActiveId,
				setActiveId,
				controlled: props.controlled || false,
			}}
			{...props}
		/>
	)
}

Accordion.Header = function ({ children, onExpand, onClick, ...rest }) {
	const { activeId, setActiveId } = React.useContext(AccordionContext)
	const expanded = rest.id === activeId

	return (
		<HeaderStyled
			expanded={expanded}
			onClick={() => {
				onClick ? onClick() : setActiveId(expanded ? "" : rest.id)
				onExpand && !expanded && onExpand()
			}}
			{...rest}
		>
			<Flex w={1} alignItems="center">
				{children}
			</Flex>
			<Icon icon="chevron-down" size={2.5} />
		</HeaderStyled>
	)
} as React.FC<AccordionHeaderProps>

Accordion.Panel = function (props) {
	const { activeId } = React.useContext(AccordionContext)

	return (
		<PanelStyled expanded={props.id === activeId} {...props}>
			<PanelInnerStyled>{props.children}</PanelInnerStyled>
		</PanelStyled>
	)
} as React.FC<AccordionPanelProps>

export { Accordion }
